import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TaskyButton, TaskyCard, TaskyDialog } from '../tasky';
import { useToast } from '../../contexts/ToastContext';
import {
  createBannerAbTest,
  createBannerAbTestLoop,
  estimateBannerAbTest,
  estimateBannerAbTestLoop,
} from '../../services/api/bannerAbTestService';
import {
  BANNER_AB_DISCLAIMER,
  DEFAULT_MAX_ROUNDS,
  DEFAULT_PERSONA_COUNT,
  MAX_ROUNDS_OPTIONS,
  PERSONA_COUNT_OPTIONS,
  type BannerAbTestEstimate,
  type BannerAbTestMode,
} from '../../types/bannerAbTest';

interface Props {
  open: boolean;
  onClose: () => void;
  /** 既定は pairwise（バナー広告ページの一括選択から起動） */
  mode?: BannerAbTestMode;
  /** pairwise 用。同一参照を保つこと（state 配列か useMemo） */
  imageIds?: number[];
  /** auto_loop 用の王者バナー */
  championImageId?: number;
  onStarted: (id: number) => void;
}

const OPTION_CLASS = (selected: boolean) =>
  `flex items-center justify-center gap-1 border rounded-md py-2 cursor-pointer ${
    selected ? 'border-primary bg-primary/5' : 'border-warm-border hover:bg-warm-surface'
  }`;

/**
 * オートバナーABテストの開始ダイアログ。
 * pairwise は候補2〜4枚のペア比較、auto_loop は王者1枚からの自動改善ループ。
 * 条件が変わるたびに見積りを取り直し、表示中の金額が今の条件のものでない限り開始させない。
 */
export const BannerAbTestStartModal: React.FC<Props> = ({
  open, onClose, mode = 'pairwise', imageIds, championImageId, onStarted,
}) => {
  const isLoop = mode === 'auto_loop';
  const ids = useMemo(() => imageIds ?? [], [imageIds]);
  const [personaCount, setPersonaCount] = useState<number>(DEFAULT_PERSONA_COUNT);
  const [maxRounds, setMaxRounds] = useState<number>(DEFAULT_MAX_ROUNDS);
  // 入力中の文字列と、見積りに反映済みの値を分ける（1文字ごとに API を叩かないため）
  const [budgetInput, setBudgetInput] = useState('');
  const [budget, setBudget] = useState<number | null>(null);
  const [estimate, setEstimate] = useState<BannerAbTestEstimate | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  // 開閉のたびに前回の選択・見積り・送信中フラグを捨てる。閉じた時点で戻すので、開き直した
  // 最初のレンダーには既に既定値が入っていて、前回の条件での見積り取得が走らない。
  // 送信中フラグは成功時に戻さない（親が閉じるまで二重送信を防ぐ）ので、ここで戻さないと再オープン時に押せなくなる。
  useEffect(() => {
    setPersonaCount(DEFAULT_PERSONA_COUNT);
    setMaxRounds(DEFAULT_MAX_ROUNDS);
    setBudgetInput('');
    setBudget(null);
    setEstimate(null);
    setError(null);
    setSubmitting(false);
  }, [open]);

  const ready = isLoop ? championImageId != null : ids.length >= 2;

  // 条件が変わったら表示中の金額は即座に捨てる。古い金額を見たまま開始できてしまうのを防ぐ。
  useEffect(() => {
    if (!open || !ready) return;
    let active = true;
    setLoading(true);
    setEstimate(null);
    setError(null);
    // 成否それぞれの中で loading まで畳む。finally に分けると1マイクロタスク遅れて別の更新になる。
    const request = isLoop
      ? estimateBannerAbTestLoop(championImageId as number, personaCount, maxRounds, budget)
      : estimateBannerAbTest(ids, personaCount);
    request
      .then((e) => {
        if (!active) return;
        setEstimate(e);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setEstimate(null);
        setError('見積りの取得に失敗しました');
        setLoading(false);
      });
    return () => { active = false; };
  }, [open, ready, isLoop, ids, championImageId, personaCount, maxRounds, budget]);

  // 空欄・0 以下・数値でない入力は「上限なし」に倒す
  const commitBudget = useCallback(() => {
    const parsed = Number.parseInt(budgetInput, 10);
    setBudget(Number.isFinite(parsed) && parsed > 0 ? parsed : null);
  }, [budgetInput]);

  const start = () => {
    if (submitting || !ready) return;
    setSubmitting(true);
    const request = isLoop
      ? createBannerAbTestLoop(championImageId as number, personaCount, maxRounds, budget)
      : createBannerAbTest(ids, personaCount);
    request
      .then((created) => {
        // 成功時は submitting を戻さない。親がダイアログを閉じるまでの二重送信を防ぐ。
        onStarted(created.id);
      })
      .catch((e: any) => {
        showToast(e?.response?.data?.message || 'ABテストの開始に失敗しました', 'error');
        setSubmitting(false);
      });
  };

  // estimateLoop() は total だけを予算で頭打ちにし、votes / images / hypothesis は
  // 「最大ラウンド × 1ラウンド」の理論値を返す。内訳の合計が total を超えたら予算で切られている
  const breakdownTotal = estimate
    ? estimate.votes + (estimate.images ?? 0) + (estimate.hypothesis ?? 0) + (estimate.needs_pool ? estimate.pool : 0)
    : 0;
  const cappedByBudget = isLoop && estimate !== null && budget !== null && estimate.total < breakdownTotal;

  return (
    <TaskyDialog
      open={open}
      onClose={onClose}
      title={isLoop ? 'オートABテスト（自動改善ループ）' : 'オートバナーABテスト'}
      size="md"
      footer={
        <>
          <TaskyButton type="button" variant="outline" size="sm" onClick={onClose}>キャンセル</TaskyButton>
          {/* 画面に出ている金額が今の条件のものでない限り開始させない（API が返す persona_count / rounds で判定）。 */}
          <TaskyButton
            type="button"
            size="sm"
            onClick={start}
            disabled={submitting || loading || !estimate || estimate.persona_count !== personaCount || (isLoop && estimate.rounds !== maxRounds)}
          >
            開始する
          </TaskyButton>
        </>
      }
    >
      <div className="space-y-4 text-sm text-gray-700">
        {isLoop ? (
          <p>
            選んだバナーを起点に、AI が改善案を {estimate?.challengers ?? '-'} 枚ずつ作り、仮想顧客に採点させます。
          </p>
        ) : (
          <p>選んだ {ids.length} 枚のバナーを、LLM が演じる仮想顧客集団にペア比較させます（{estimate?.comparisons ?? '-'} 比較）。</p>
        )}

        <fieldset>
          <legend className="text-xs font-semibold text-gray-500 mb-2">仮想顧客の人数</legend>
          <div className="grid grid-cols-4 gap-2">
            {PERSONA_COUNT_OPTIONS.map((n) => (
              <label key={n} className={OPTION_CLASS(personaCount === n)}>
                <input type="radio" name="persona_count" value={n} checked={personaCount === n} onChange={() => setPersonaCount(n)} className="sr-only" />
                {n}体
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1">論文では 100 体でも方向の的中率は保たれ、500 体で頭打ちになります。</p>
        </fieldset>

        {isLoop && (
          <>
            <fieldset>
              <legend className="text-xs font-semibold text-gray-500 mb-2">最大ラウンド</legend>
              <div className="grid grid-cols-5 gap-2">
                {MAX_ROUNDS_OPTIONS.map((n) => (
                  <label key={n} className={OPTION_CLASS(maxRounds === n)}>
                    <input type="radio" name="max_rounds" value={n} checked={maxRounds === n} onChange={() => setMaxRounds(n)} className="sr-only" />
                    {n}ラウンド
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">勝者が入れ替わらなくなった時点で、ここに届く前でも終了します。</p>
            </fieldset>

            <div>
              <label htmlFor="ab-budget" className="block text-xs font-semibold text-gray-500 mb-1">予算（クレジット・任意）</label>
              <input
                id="ab-budget"
                type="number"
                min={1}
                inputMode="numeric"
                value={budgetInput}
                onChange={(e) => setBudgetInput(e.target.value)}
                onBlur={commitBudget}
                placeholder="未入力なら上限なし"
                className="w-40 rounded-md border border-warm-border px-2 py-1 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                2ラウンド目以降のゲートです。1ラウンド目は予算に関係なく実行し、次のラウンドの見込みが予算を超えたらそこで打ち切って結果を出します。
              </p>
            </div>
          </>
        )}

        <TaskyCard variant="warm" padding="sm" className="space-y-1" aria-live="polite">
          {loading && <p>見積りを計算中…</p>}
          {error && <p className="text-destructive">{error}</p>}
          {estimate && !loading && (
            <>
              {isLoop && estimate.images != null && <p>派生案の生成: 約 {estimate.images} クレジット</p>}
              {isLoop && estimate.hypothesis != null && <p>改善案の検討: 約 {estimate.hypothesis} クレジット</p>}
              <p>採点: 約 {estimate.votes} クレジット</p>
              {estimate.needs_pool && <p>仮想顧客の生成（この商品の初回のみ）: 約 {estimate.pool} クレジット</p>}
              <p className="font-semibold text-dark">合計 約 {estimate.total} クレジット</p>
              {cappedByBudget && (
                <p className="text-xs text-gray-500">
                  内訳は最大ラウンドまで回した場合の額です。予算上限 {budget} クレジットで打ち切るので、実際の消費は合計までに収まります。
                </p>
              )}
            </>
          )}
        </TaskyCard>

        <p className="text-xs text-gray-500" data-testid="ab-disclaimer">{BANNER_AB_DISCLAIMER}</p>
      </div>
    </TaskyDialog>
  );
};

export default BannerAbTestStartModal;
